import {
  DropdownButton,
  type DropdownButtonProps,
  type DropdownMenuItemType,
  type IconName,
  notifySuccess,
} from '@omnipresentgroup/design-system';
import {
  useForceSyncInvoiceReportMutation,
  useXeroConnection,
} from '@omnipresentgroup/domains';
import {
  FileFormat,
  type InvoiceReport,
  InvoiceSyncStatusTarget,
  XeroConnectionStatusEnum,
} from '@omnipresentgroup/types';
import { DateTime } from 'luxon';
import { useMemo } from 'react';

import { useInvoiceDownload } from './hooks/useInvoiceDownload';

const PDF_CUTOFF_DATE = new Date('2022-02-01');
const XERO_SYNC_SUCCESS_MESSAGE =
  'Invoice synchronization with Xero has been initiated';

type Props = {
  invoice: InvoiceReport;
  icon?: IconName;
  withSelectableAction?: boolean;
} & Pick<DropdownButtonProps, 'variant'>;

export const InvoiceReportRowActions = ({
  invoice,
  icon,
  withSelectableAction,
  variant,
}: Props) => {
  const { id, issueDate, companyId, status, reference, type } = invoice;

  const xeroStatus = useMemo(
    () =>
      invoice.syncStatuses?.find(
        (syncStatus) => syncStatus.syncTarget === InvoiceSyncStatusTarget.XERO,
      ),
    [invoice.syncStatuses],
  );

  const { data: xeroConnection } = useXeroConnection({
    clientId: companyId,
    enabled: !!companyId,
  });

  const { downloadLink, downloadInvoice } = useInvoiceDownload(companyId);
  const { mutate: forceSync } = useForceSyncInvoiceReportMutation({
    companyId,
    invoiceId: id,
    onSuccess: () => {
      notifySuccess({
        title: XERO_SYNC_SUCCESS_MESSAGE,
      });
    },
  });

  const dropdownOptions: DropdownMenuItemType[] = Object.values(FileFormat).map(
    (format) => {
      const buttonIsDisabled =
        format === FileFormat.PDF &&
        DateTime.fromISO(issueDate) < DateTime.fromJSDate(PDF_CUTOFF_DATE);

      return {
        name: `Download ${format}`,
        icon: 'Download',
        disabled: buttonIsDisabled,
        onClick: () => {
          downloadInvoice(id, status, reference, type, format);
        },
      };
    },
  );

  const options = [
    ...dropdownOptions,
    ...(xeroConnection?.connectionStatus === XeroConnectionStatusEnum.CONNECTED
      ? [
          {
            name: 'Force Xero sync',
            icon: 'Refresh',
            onClick: () => {
              forceSync({
                syncTarget: InvoiceSyncStatusTarget.XERO,
              });
            },
          } as DropdownMenuItemType,
        ]
      : []),
    ...(xeroStatus?.xeroFinancialDocument?.details?.url
      ? [
          {
            name: 'View Xero document',
            icon: 'ExternalLink',
            onClick: () => {
              window.open(
                xeroStatus.xeroFinancialDocument?.details?.url,
                '_blank',
              );
            },
          } as DropdownMenuItemType,
        ]
      : []),
  ];

  return (
    <>
      <a href="#download" ref={downloadLink} hidden>
        Download Link
      </a>
      <DropdownButton
        icon={icon || 'Dots'}
        aria-label={`invoice-actions-${id}`}
        {...(withSelectableAction && { withSelectableAction })}
        variant={variant}
        options={options}
      />
    </>
  );
};
